<template>
  <div
    ref="container"
    class="pdf-reader"
    :class="[
      isMaximized ? 'pdf-reader--maximized' : 'pdf-reader--bordered',
      !isMaximized && 'resizable',
      !isMaximized && !props.initialHeight && 'pdf-reader--full-height'
    ]"
    :style="containerStyle"
  >
    <div ref="toolbarRef" class="toolbar">
      <button class="btn" @click="zoomOut" aria-label="Zoom out">−</button>
      <span class="zoom-label">{{ scale }}%</span>
      <button class="btn" @click="zoomIn" aria-label="Zoom in">+</button>
      <button class="btn" @click="resetZoom" aria-label="Reset zoom">Reset</button>
      <span class="toolbar-sep" aria-hidden="true" />
      <button class="btn" @click="download" aria-label="Download">Download</button>
      <button class="btn" @click="printFile" aria-label="Print">Print</button>
      <div class="btn-group">
        <button
          class="btn-group-item"
          :class="{ active: fitMode === 'width' }"
          @click="setFitMode('width')"
          aria-label="Fit Width"
          title="Fit Width"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 12h14" />
            <path d="M5 12l4-4" />
            <path d="M5 12l4 4" />
            <path d="M19 12l-4-4" />
            <path d="M19 12l-4 4" />
          </svg>
        </button>
        <button
          class="btn-group-item"
          :class="{ active: fitMode === 'height' }"
          @click="setFitMode('height')"
          aria-label="Fit Page"
          title="Fit Page"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M12 5l-4 4" />
            <path d="M12 5l4 4" />
            <path d="M12 19l-4-4" />
            <path d="M12 19l4-4" />
          </svg>
        </button>
      </div>
      <button class="btn" @click="toggleMaximized" :aria-label="isMaximized ? 'Exit Full Screen' : 'Full Screen'">
        <svg
          v-if="!isMaximized"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
        </svg>
        <svg
          v-else
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
        </svg>
      </button>
      <button class="btn" v-if="initialOnly && !loadAll && pageCount > 1" @click="loadAll = true">
        Load all pages
      </button>
      <slot name="actions" />
    </div>

    <div ref="pagesContainer" class="pages stable-scrollbar">
      <div
        class="page"
        v-for="p in pageList"
        :key="p"
        :class="{ 'page--gap': pageList.length > 1 && p !== pageList[pageList.length - 1] }"
        :style="{ width: pageWidth, transform: `scale(${scale / 100})`, transformOrigin: 'top center' }"
      >
        <component
          :is="VuePDF"
          v-if="pdf"
          :pdf="pdf"
          :page="p"
          :text-layer="true"
          :width="parseInt(pageWidth)"
          :key="`${effectiveFitMode}-${pageWidth}`"
        />
        <div v-else class="page-loading">Loading…</div>
      </div>
      <div v-if="!pageList.length" class="page-empty">No pages</div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { VuePDF, usePDF } from '@tato30/vue-pdf'
import { useElementSize } from '@vueuse/core'

const props = defineProps({
  src: {
    type: String,
    required: true
  },
  initialOnly: {
    type: Boolean,
    default: false
  },
  initialFit: {
    type: String,
    default: 'width'
  },
  initialHeight: {
    type: [String, Number],
    default: undefined
  },
  initialWidth: {
    type: [String, Number],
    default: undefined
  },
  resizeGap: {
    type: [String, Number],
    default: '6px'
  },
  // Responsive behavior (recommended):
  // - On narrow containers (mobile), Fit Height is clamped to Fit Width to prevent horizontal overflow.
  // - On larger containers, the chosen fit mode is used as-is.
  responsiveFit: {
    type: Boolean,
    default: true
  },
  // Container width threshold in px below which Fit Height clamps to Fit Width.
  responsiveFitBreakpointPx: {
    type: Number,
    default: 640
  }
})

// 1. We measure 'container' (the root) instead of 'pagesContainer'.
// This prevents the infinite loop caused by scrollbars appearing/disappearing.
const container = ref(null)
const pagesContainer = ref(null)
const toolbarRef = ref(null)
const { width: rootWidth, height: rootHeight } = useElementSize(container, undefined, { box: 'border-box' })
const { height: toolbarHeight } = useElementSize(toolbarRef, undefined, { box: 'border-box' })

const isMaximized = ref(false)
function toggleMaximized() {
  isMaximized.value = !isMaximized.value
}

const source = computed(() => props.src)
const { pdf } = usePDF(source)

const pageCount = ref(0)
const aspectRatio = ref(0.75)

watch(pdf, async (task) => {
  try {
    const doc = task ? await task.promise : null
    pageCount.value = doc?.numPages ?? 0
    if (doc && pageCount.value > 0) {
      const page = await doc.getPage(1)
      const viewport = page.getViewport({ scale: 1 })
      if (viewport.width && viewport.height) {
        aspectRatio.value = viewport.width / viewport.height
      }
    }
  } catch {
    pageCount.value = 0
  }
}, { immediate: true })

const loadAll = ref(false)
const pageList = computed(() => {
  if (props.initialOnly && !loadAll.value && pageCount.value > 0) return [1]
  return Array.from({ length: pageCount.value }, (_, i) => i + 1)
})

const fitMode = ref(props.initialFit || 'width')

/**
 * Effective fit mode respects mobile constraints.
 * On narrow containers, we clamp fit-height to fit-width to avoid horizontal overflow.
 */
const effectiveFitMode = computed(() => {
  if (!props.responsiveFit) return fitMode.value
  if (fitMode.value !== 'height') return fitMode.value
  const bp = props.responsiveFitBreakpointPx ?? 640
  // If the component is not yet measured, don’t force a mode.
  if (!rootWidth.value) return fitMode.value
  return rootWidth.value < bp ? 'width' : 'height'
})
const manualWidth = ref(0)
const containerStyle = computed(() => {
  if (isMaximized.value) return {}
  const style = {}
  if (props.initialHeight != null) {
    style.height = typeof props.initialHeight === 'number' ? `${props.initialHeight}px` : String(props.initialHeight)
  }
  if (props.initialWidth != null) {
    style.width = typeof props.initialWidth === 'number' ? `${props.initialWidth}px` : String(props.initialWidth)
  }
  // Add bottom padding to create separation from the resize handle to inner scrollbars
  style.paddingBottom = typeof props.resizeGap === 'number' ? `${props.resizeGap}px` : (props.resizeGap ?? '6px')
  return style
})

function setFitMode(mode) {
  if (fitMode.value === mode) {
    if (pagesContainer.value) {
      const pageEl = pagesContainer.value.querySelector('.page')
      if (pageEl) {
        manualWidth.value = pageEl.clientWidth
      } else {
        manualWidth.value = Math.max(pagesContainer.value.clientWidth - 32, 320)
      }
    }
    fitMode.value = 'manual'
  } else {
    fitMode.value = mode
    scale.value = 100
  }
}

const scale = ref(100)
function zoomIn() {
  scale.value = Math.min(200, scale.value + 10)
}
function zoomOut() {
  scale.value = Math.max(50, scale.value - 10)
}
function resetZoom() {
  scale.value = 100
}

// This is the fixed logic for sizing
const pageWidth = computed(() => {
  if (fitMode.value === 'manual') return `${manualWidth.value}px`

  // CORRECTED MATH: p-4 (16px top) + pb-5 (20px bottom).
  // pb-5 overrides p-4 bottom, it does NOT add to it.
  const paddingHeight = 36

  // We calculate available space based on the ROOT container.
  const availableWidth = Math.max(rootWidth.value - 32, 100)
  // Subtract resize gap (only supports px or number); defaults to 6px if unspecified
  const gapPx = typeof props.resizeGap === 'number'
    ? props.resizeGap
    : (/^\d+(?:\.\d+)?px$/.test(String(props.resizeGap)) ? parseFloat(String(props.resizeGap)) : 6)
  const availableHeight = Math.max(rootHeight.value - (toolbarHeight.value || 0) - paddingHeight - gapPx, 100)

  if (effectiveFitMode.value === 'width') {
    return `${availableWidth}px`
  }

  // fit-height logic
  // We use the stable rootHeight, so even if a scrollbar appears, this value doesn't flutter.
  const widthFromHeight = availableHeight * aspectRatio.value

  // Safety clamp: even on larger screens, never exceed available width.
  // This prevents tiny overflow caused by rounding / scrollbars / zoom.
  return `${Math.min(widthFromHeight, availableWidth)}px`
})

function download() {
  const a = document.createElement('a')
  a.href = props.src
  a.download = 'document.pdf'
  a.target = '_blank'
  a.rel = 'noopener'
  a.click()
}

function printFile() {
  const w = window.open(props.src, '_blank', 'noopener')
  if (w) w.focus()
}
</script>

<style scoped>
/* ── Root container ───────────────────────────────────────── */
.pdf-reader {
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  overflow: hidden;
  max-width: 100%;
}

.pdf-reader--full-height {
  height: 100%;
}

.pdf-reader--bordered {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.pdf-reader--maximized {
  position: fixed;
  inset: 0;
  z-index: 2000;
  width: 100vw;
  height: 100vh;
  border-radius: 0;
  border: none;
}

/* ── Toolbar ──────────────────────────────────────────────── */
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 10;
}

/* All direct children hold their size — items wrap rather than shrink */
.toolbar > * {
  flex-shrink: 0;
}

.zoom-label {
  font-size: 0.875rem;
  color: #6b7280;
  width: 3.5rem;
  text-align: center;
  user-select: none;
}

.toolbar-sep {
  display: block;
  height: 1.25rem;
  width: 1px;
  background: #e5e7eb;
}

/* ── Buttons ──────────────────────────────────────────────── */
.btn {
  height: 2rem;
  padding: 0 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  font-size: 0.875rem;
  line-height: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.btn:hover {
  background: #f9fafb;
}

/* ── Button group (fit width / fit height) ────────────────── */
.btn-group {
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
}

.btn-group-item {
  height: 2rem;
  padding: 0 0.5rem;
  background: #ffffff;
  color: #6b7280;
  display: inline-grid;
  place-items: center;
  border: none;
  cursor: pointer;
}

.btn-group-item:hover {
  background: #f9fafb;
}

.btn-group-item.active {
  background: #f3f4f6;
  color: #111827;
  box-shadow: inset 0 2px 4px 0 rgb(0 0 0 / 0.05);
}

.btn-group-item:first-child {
  border-right: 1px solid #e5e7eb;
}

/* ── Pages area ───────────────────────────────────────────── */
.pages {
  flex: 1;
  overflow: auto;
  background: #f9fafb;
  padding: 1rem 1rem 1.25rem;
  min-height: 0;
}

/* ── Individual page ──────────────────────────────────────── */
.page {
  /* Width is set via inline style (pageWidth computed); transform handles zoom */
  margin-left: auto;
  margin-right: auto;
  background: #ffffff;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  overflow: hidden;
}

.page--gap {
  margin-bottom: 1rem;
}

.page-loading {
  height: 600px;
  display: grid;
  place-items: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.page-empty {
  height: 100%;
  display: grid;
  place-items: center;
  color: #6b7280;
}

/* ── Resizable handle ─────────────────────────────────────── */
.resizable {
  resize: both;
  min-width: 320px;
  min-height: 240px;
}

/* ── Stable scrollbar ─────────────────────────────────────── */
.stable-scrollbar {
  scrollbar-gutter: stable;
}

@supports not (scrollbar-gutter: stable) {
  .stable-scrollbar {
    overflow-y: scroll;
  }
}

/* ── Mobile: tighter controls ─────────────────────────────── */
@media (max-width: 639px) {
  .toolbar {
    gap: 0.375rem;
  }

  .toolbar-sep {
    display: none;
  }

  .btn {
    height: 1.75rem;
    padding: 0 0.5rem;
    font-size: 0.75rem;
  }

  .btn-group-item {
    height: 1.75rem;
  }
}
</style>
